import React from "react"
import { ArrowRight, ExternalLink, TrendingUp } from "lucide-react"
import { Link } from "react-router-dom"
import { trackEvent } from "../utils/tracking"
import { normalizeGames } from "../utils/games"

const formatRtpUp = (rawRtp) => {
  const parsed = Number.parseFloat(rawRtp)
  if (!Number.isFinite(parsed)) return null
  return (Math.ceil(parsed * 10) / 10).toFixed(1)
}

const formatGame = (game) => {
  const normalized = String(game).toLowerCase()
  if (normalized === "cs" || normalized === "csgo") return "CS"
  if (normalized === "rust") return "RUST"
  return String(game).toUpperCase()
}

const WebsiteDetails = ({
  websiteName,
  websiteRTP,
  websiteLogo,
  websiteGames = [],
  websiteDescription = "",
  totalCases,
  subpageLink,
  affiliateLink,
}) => {
  const formattedRtp = formatRtpUp(websiteRTP)
  const formattedGames = [...new Set(normalizeGames(websiteGames).map(formatGame))]
  const fallbackSlug = String(websiteName || "").toLowerCase().replace(/[^a-z0-9]+/g, "")
  const casesLink = subpageLink || `/websites/${fallbackSlug}`
  const hasCaseCount = Number.isFinite(Number(totalCases))

  return (
    <article className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-[#2A2D31]/60 bg-gradient-to-b from-[#171A1E]/95 to-[#0D0F11]/95 shadow-[0_10px_30px_rgba(0,0,0,0.16)] backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-[#FF6B00]/40 hover:shadow-[0_16px_40px_rgba(255,107,0,0.14)]">
      <div className="absolute inset-x-8 top-0 z-20 h-px bg-gradient-to-r from-transparent via-[#FF9A3D]/70 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

      <div className="relative flex h-40 items-center justify-center overflow-hidden border-b border-[#2A2D31]/40 bg-black p-6">
        <img
          src={websiteLogo || "/placeholder.svg"}
          alt={`${websiteName} logo`}
          className="relative z-10 h-full w-full object-contain transition-transform duration-500 ease-out group-hover:scale-105"
        />

        <div className="absolute right-3 top-3 z-20 inline-flex items-center gap-1.5 rounded-full border border-[#FF6B00]/30 bg-[#0A0C10]/85 px-2.5 py-1 text-xs font-semibold text-[#FF9A3D] shadow-[0_4px_16px_rgba(0,0,0,0.35)] backdrop-blur-md">
          <TrendingUp className="h-3.5 w-3.5" />
          {formattedRtp ? `${formattedRtp}% RTP` : "RTP N/A"}
        </div>

        {formattedGames.length > 0 && (
          <div className="absolute bottom-3 left-3 z-20 flex flex-wrap gap-1.5">
            {formattedGames.map((game) => (
              <span
                key={game}
                className="rounded-full border border-[#FF6B00]/25 bg-[#0A0C10]/90 px-2.5 py-1 text-[11px] font-semibold tracking-wider text-[#FF9A3D] backdrop-blur-sm"
              >
                {game}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col p-5 text-white">
        <h3 className="text-center font-display text-xl font-semibold text-white transition-colors duration-300 group-hover:text-[#FFB26F]">
          {websiteName}
        </h3>

        <p className="mt-4 line-clamp-3 min-h-[3.75rem] text-center text-sm leading-5 text-blue-100/65">
          {websiteDescription || `Browse cases from ${websiteName} and compare their RTP and site prices.`}
        </p>

        {hasCaseCount && (
          <p className="mt-3 text-center text-xs font-medium uppercase tracking-wider text-blue-100/45">
            {Number(totalCases)} {Number(totalCases) === 1 ? "case" : "cases"} tracked
          </p>
        )}

        <div className="mt-auto grid grid-cols-2 gap-2.5 pt-5">
          <Link
            to={casesLink}
            className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-[#FF6B00]/40 bg-[#FF6B00]/5 px-3 py-2.5 text-sm font-semibold text-[#FF9A3D] transition-all hover:border-[#FF6B00]/70 hover:bg-[#FF6B00]/10"
          >
            View Cases
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
          </Link>

          <a
            href={affiliateLink}
            target="_blank"
            rel="noopener noreferrer sponsored"
            data-popup-gate="true"
            data-affiliate-outbound="true"
            data-affiliate-website={websiteName}
            aria-disabled={!affiliateLink}
            className={`inline-flex items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-[#FF6B00] to-[#FF9A3D] px-3 py-2.5 text-sm font-semibold text-white transition-all hover:brightness-105 ${
              !affiliateLink ? "pointer-events-none opacity-50" : ""
            }`}
            onClick={() => {
              try {
                trackEvent("AffiliateClick", { website: websiteName, url: affiliateLink })
              } catch {
                // Tracking should never block navigation.
              }
            }}
          >
            Visit Site
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </div>
    </article>
  )
}

export default WebsiteDetails
